import React, { useState, useEffect } from "react";
import { X, ChevronRight, ArrowLeft, ImageOff } from "lucide-react";

const isVideo = (url) => /\.(mp4|mov|webm|avi)/i.test(url || "") || (url || "").includes("/video/");

export function MediaLightbox({ items = [], startIdx = 0, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const item = items[idx];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowRight")  setIdx(i => Math.min(i + 1, items.length - 1));
      if (e.key === "ArrowLeft")   setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items.length, onClose]);

  return (
    <div className="fixed inset-0 bg-black/95 z-[200] flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <div className="text-white/70 text-sm font-medium">{idx + 1} / {items.length}</div>
        <button onClick={onClose} className="text-white/60 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Main media */}
      <div className="flex-1 flex items-center justify-center p-4 min-h-0" onClick={e => e.stopPropagation()}>
        {isVideo(item) ? (
          <video src={item} controls autoPlay className="max-w-full max-h-full rounded-xl shadow-2xl" />
        ) : (
          <img
            src={item}
            alt={`Media ${idx + 1}`}
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
          />
        )}
      </div>

      {/* Navigation strip */}
      {items.length > 1 && (
        <div className="flex-shrink-0 flex items-center justify-center gap-3 pb-4 px-5" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setIdx(i => Math.max(i - 1, 0))}
            disabled={idx === 0}
            className="text-white/50 hover:text-white disabled:opacity-20 p-2 rounded-lg hover:bg-white/10 transition-all"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div className="flex gap-2 overflow-x-auto max-w-xl py-1">
            {items.map((url, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  i === idx ? "border-[#F97316] opacity-100" : "border-transparent opacity-40 hover:opacity-80"
                }`}
              >
                {isVideo(url) ? (
                  <div className="w-full h-full bg-gray-700 flex items-center justify-center text-white text-xs">▶</div>
                ) : (
                  <img src={url} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>

          <button
            onClick={() => setIdx(i => Math.min(i + 1, items.length - 1))}
            disabled={idx === items.length - 1}
            className="text-white/50 hover:text-white disabled:opacity-20 p-2 rounded-lg hover:bg-white/10 transition-all"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * MediaGrid — thumbnail grid with lightbox on click.
 * When no photos exist, shows an empty-state frame.
 */
export function MediaGrid({ urls, room }) {
  const [lightbox, setLightbox] = useState(null);
  const photos = urls || [];

  if (!photos.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-5 px-3 text-center mt-1">
        <ImageOff className="h-6 w-6 text-gray-300" />
        <p className="text-xs text-gray-400 leading-snug">No photos attached<br />by broker / agent</p>
      </div>
    );
  }

  return (
    <>
      {lightbox != null && (
        <MediaLightbox items={photos} startIdx={lightbox} onClose={() => setLightbox(null)} />
      )}
      <div className="grid grid-cols-3 gap-1.5 mt-1">
        {photos.map((url, i) =>
          isVideo(url) ? (
            <div key={i} onClick={() => setLightbox(i)}
              className="relative w-full aspect-video bg-black rounded-lg overflow-hidden cursor-pointer group border border-gray-200">
              <video src={url} className="w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0 flex items-center justify-center group-hover:bg-black/20 transition-all">
                <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center backdrop-blur-sm">
                  <span className="text-white text-sm ml-0.5">▶</span>
                </div>
              </div>
            </div>
          ) : (
            <button key={i} onClick={() => setLightbox(i)}
              className="w-full aspect-video rounded-lg overflow-hidden border border-gray-200 hover:border-[#F97316]/60 hover:scale-[1.03] transition-all">
              <img src={url} alt={`${room} ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          )
        )}
      </div>
    </>
  );
}
